if (!customElements.get('blog-ingredient-filter')) {
  class BlogIngredientFilter extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.main-blog');
      this.grid = this.section.querySelector('.main-blog__items');
      this.input = this.querySelector('[data-ingredient-input]');
      this.chips = Array.from(this.querySelectorAll('[data-ingredient]'));
      this.status = this.querySelector('[data-ingredient-status]');
      this.clearButton = this.querySelector('[data-ingredient-clear]');
      this.emptyMessage = this.section.querySelector('[data-ingredient-empty]');
      this.pages = parseInt(this.dataset.pages, 10) || 1;
      this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.loadingPromise = null;
      // Keep the original page so it can be restored when the filter is cleared
      this.originalColumns = Array.from(this.grid.querySelectorAll('[data-article-id]'));
      this.allColumns = this.pages <= 1 ? this.originalColumns : null;
    }

    connectedCallback() {
      let timer;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.update(), 250);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });
      this.chips.forEach((chip) =>
        chip.addEventListener('click', () => {
          const pressed = chip.getAttribute('aria-pressed') !== 'true';
          chip.setAttribute('aria-pressed', pressed);
          chip.classList.toggle('btn--active', pressed);
          this.update();
        })
      );
      this.clearButton.addEventListener('click', () => this.clear());

      this.restoreFromUrl();
    }

    restoreFromUrl() {
      const param = new URLSearchParams(window.location.search).get('ingredienten');
      if (!param) return;

      const typed = [];
      param.split(',').forEach((value) => {
        const term = value.trim().toLowerCase();
        if (!term) return;
        const chip = this.chips.find((c) => c.dataset.ingredient === term);
        if (chip) {
          chip.setAttribute('aria-pressed', 'true');
          chip.classList.add('btn--active');
        } else {
          typed.push(term);
        }
      });
      this.input.value = typed.join(', ');
      this.update();
    }

    getTerms() {
      const chipTerms = this.chips
        .filter((chip) => chip.getAttribute('aria-pressed') === 'true')
        .map((chip) => chip.dataset.ingredient);
      const typedTerms = this.input.value.split(',');
      return [...new Set([...chipTerms, ...typedTerms].map((t) => t.trim().toLowerCase()).filter(Boolean))];
    }

    async update() {
      const terms = this.getTerms();
      this.updateUrl(terms);

      if (!terms.length) {
        this.reset();
        return;
      }

      if (!this.allColumns) {
        this.status.textContent = '…';
        await this.loadAllPages();
        // Terms may have changed while the pages were loading
        if (this.getTerms().join(',') !== terms.join(',')) return;
      }
      this.grid.append(...this.allColumns);

      const matchers = terms.map((term) => this.createMatcher(term));
      let count = 0;
      this.allColumns.forEach((column) => {
        const text = this.normalize(column.dataset.ingredients || '');
        const match = matchers.every((matches) => matches(text));
        column.hidden = !match;
        if (match) count++;
      });

      this.toggleFilteredMode(true);
      this.status.textContent = (this.dataset.resultsText || '[count]').replace('[count]', count);
      if (this.emptyMessage) this.emptyMessage.hidden = count > 0;
    }

    reset() {
      this.grid.querySelectorAll('[data-article-id]').forEach((column) => {
        if (!this.originalColumns.includes(column)) column.remove();
      });
      this.originalColumns.forEach((column) => {
        column.hidden = column.hasAttribute('data-offset-article');
      });
      this.toggleFilteredMode(false);
      this.status.textContent = '';
      if (this.emptyMessage) this.emptyMessage.hidden = true;
    }

    clear() {
      this.input.value = '';
      this.chips.forEach((chip) => {
        chip.setAttribute('aria-pressed', 'false');
        chip.classList.remove('btn--active');
      });
      this.update();
    }

    toggleFilteredMode(active) {
      this.section.classList.toggle('main-blog--ingredient-filtered', active);
      this.clearButton.hidden = !active;
    }

    updateUrl(terms) {
      const url = new URL(window.location.href);
      if (terms.length) {
        url.searchParams.set('ingredienten', terms.join(','));
      } else {
        url.searchParams.delete('ingredienten');
      }
      window.history.replaceState(window.history.state, '', url.toString());
    }

    loadAllPages() {
      if (this.loadingPromise) return this.loadingPromise;

      const requests = [];
      for (let page = 1; page <= this.pages; page++) {
        if (page === this.currentPage) {
          requests.push(Promise.resolve(this.originalColumns));
          continue;
        }
        const url = `${window.location.pathname}?page=${page}&section_id=${this.dataset.sectionId}`;
        requests.push(
          fetch(url)
            .then((response) => response.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              return Array.from(doc.querySelectorAll('.main-blog__items [data-article-id]'));
            })
            .catch(() => [])
        );
      }

      this.loadingPromise = Promise.all(requests).then((pages) => {
        this.allColumns = pages
          .flat()
          .map((column) => (column.ownerDocument === document ? column : document.importNode(column, true)));
      });
      return this.loadingPromise;
    }

    normalize(text) {
      return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
    }

    createMatcher(term) {
      const normalized = this.normalize(term);
      // Short terms like "ei" or "ui" only match at the start of a word, so "prei" doesn't count as "ei"
      if (normalized.length < 4) {
        const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|[^a-z])${escaped}`);
        return (text) => regex.test(text);
      }
      // Rough Dutch plural handling: "tomaat" also finds "tomaten", "champignons" also finds "champignon"
      const stem = normalized.replace(/(jes|en|s)$/, '').replace(/([aeou])\1(?=[^aeiou]*$)/, '$1');
      const variants = [normalized];
      if (stem.length >= 4) variants.push(stem);
      return (text) => variants.some((variant) => text.includes(variant));
    }
  }

  customElements.define('blog-ingredient-filter', BlogIngredientFilter);
}
